import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

// Increase this for wider sidebar
const SIDEBAR_WIDTH_MOBILE = '300px';
const SIDEBAR_WIDTH_DESKTOP = '280px';

const DashboardLayout = ({ children, handleLogout, isDarkMode, toggleDarkMode, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isAdmin = userData?.role === 'admin' || userData?.role === 'moderator';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  const handleLogoutClick = () => {
    if (handleLogout) {
      handleLogout();
    } else {
      navigate('/');
    }
  };

  // Determine current view based on pathname
  const getCurrentView = () => {
    if (location.pathname === '/dashboard' || location.pathname === '/') return 'dashboard';
    if (location.pathname === '/assign-task') return 'assign-task';
    return null;
  };

  const currentView = getCurrentView();

  const roleBadge = (() => {
    if (userData?.role === 'admin') {
      return { label: 'Admin', icon: 'fas fa-crown', iconColor: '#fcd34d' };
    }
    if (userData?.role === 'moderator') {
      return { label: 'Moderator', icon: 'fas fa-star', iconColor: '#facc15' };
    }
    return { label: 'User', icon: 'fas fa-user', iconColor: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : '#1f2937' };
  })();

  // Theme variables
  const themeBg = isDarkMode
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)';

  // --- New style variables for cleaner code ---
  const sidebarWidth = isMobile ? SIDEBAR_WIDTH_MOBILE : SIDEBAR_WIDTH_DESKTOP;
  const mainContentMargin = isMobile ? '0' : sidebarWidth;
  const buttonPadding = isMobile ? '0.875rem 1.125rem' : '1.125rem'; // Slightly more padding
  const buttonGap = isMobile ? '0.75rem' : '1rem'; // Slightly more gap

  return (
    <div className={`dashboard-layout ${isDarkMode ? 'dark-mode' : ''}`} style={{
      minHeight: '100vh',
      background: themeBg,
      display: 'flex',
      width: '100%',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Mobile Hamburger Menu Button */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 1001,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
            borderRadius: '12px',
            width: '46px',
            height: '46px',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDarkMode ? '#818cf8' : '#667eea',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isDarkMode
              ? '0 4px 15px rgba(0, 0, 0, 0.35)'
              : '0 4px 15px rgba(102, 126, 234, 0.2)',
            transform: sidebarOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            boxSizing: 'border-box'
          }}
          className="mobile-menu-btn"
          title={sidebarOpen ? "Close menu" : "Open menu"}
        >
          <i 
            className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`}
            style={{
              fontSize: '1.25rem',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1em',
              height: '1em',
              margin: 0,
              padding: 0,
              textAlign: 'center'
            }}
          ></i>
        </button>
      )}

      {/* Dark Mode Toggle Button — Floating at top-right for both Desktop & Mobile */}
      {toggleDarkMode && (
        <button
          onClick={toggleDarkMode}
          style={{
            position: 'fixed',
            top: isMobile ? '1rem' : '1.25rem',
            right: isMobile ? '1rem' : '2rem',
            zIndex: 1001,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 193, 7, 0.2) 100%)'
              : 'rgba(255, 255, 255, 0.95)',
            border: `2px solid ${isDarkMode ? 'rgba(255, 215, 0, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
            borderRadius: '12px',
            width: '46px',
            height: '46px',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDarkMode ? '#ffd700' : '#667eea',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: isDarkMode
              ? '0 4px 15px rgba(255, 215, 0, 0.3)'
              : '0 4px 15px rgba(102, 126, 234, 0.2)',
            boxSizing: 'border-box'
          }}
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.06)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <i 
            className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}
            style={{
              fontSize: '1.2rem',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1em',
              height: '1em',
              margin: 0,
              padding: 0,
              textAlign: 'center'
            }}
          ></i>
        </button>
      )}



      {/* Sidebar Overlay for Mobile (No changes needed) */}
      {sidebarOpen && isMobile && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998
          }}
          className="sidebar-overlay"
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'show' : ''}`}
        style={{
          position: 'fixed',
          top: '0',
          left: isMobile ? (sidebarOpen ? '0' : `-${SIDEBAR_WIDTH_MOBILE}`) : '0', // UPDATED width here
          bottom: '0',
          width: sidebarWidth, // USING NEW VARIABLE
          height: '100vh',
          maxHeight: '100vh',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(44, 62, 80, 0.95) 0%, rgba(52, 152, 219, 0.95) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 999,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowY: 'auto',
          overflowX: 'hidden',
          transform: 'none',
          willChange: 'left',
          borderRight: isDarkMode
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: isDarkMode
            ? '4px 0 30px rgba(0, 0, 0, 0.5), inset -1px 0 0 rgba(255, 255, 255, 0.1)'
            : '4px 0 30px rgba(0, 0, 0, 0.3), inset -1px 0 0 rgba(255, 255, 255, 0.2)',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Sidebar Content */}
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Logo Section (No changes needed) */}
          <div style={{
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem 0',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}>
              <Logo size="default" isDarkMode={isDarkMode} />
            </div>
          </div>

          {/* User Profile Card (Simplified Header Element) */}
          {userData && (
            <div style={{
              background: isDarkMode
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(255, 255, 255, 0.25)',
              padding: '0.875rem 1rem',
              borderRadius: '0.875rem',
              marginBottom: '1.25rem',
              border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.15)'
                : '1px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `2px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.8)'}`,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                  flexShrink: 0
                }}>
                  <img
                    src={userData.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.fullname || userData.name || 'User')}&background=667eea&color=fff&size=128`}
                    alt={userData.fullname || userData.name || 'User'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '0.9375rem',
                      fontWeight: '700',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2
                    }}>
                      {userData.fullname || userData.name || 'User'}
                    </h3>
                    <span style={{
                      padding: '0.15rem 0.5rem',
                      borderRadius: '8px',
                      fontSize: '0.625rem',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      background: userData.role === 'admin' || userData.role === 'moderator'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {roleBadge.label}
                    </span>
                  </div>
                  <button
                    onClick={() => handleNavigation('/update-password')}
                    style={{
                      marginTop: '0.25rem',
                      background: 'none',
                      border: 'none',
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'block'
                    }}
                  >
                    Change password
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: buttonGap, // USING NEW VARIABLE
            marginBottom: 'auto',
            width: '100%'
          }}>
            {/* Dashboard - Admin Only */}
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/dashboard')}
                className="btn-3d"
                style={{
                  padding: buttonPadding, // USING NEW VARIABLE
                  background: currentView === 'dashboard'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  borderRadius: isMobile ? '0.875rem' : '0.75rem',
                  border: `1px solid ${currentView === 'dashboard' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: buttonGap, // USING NEW VARIABLE
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '100%',
                  textAlign: 'left',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: currentView === 'dashboard'
                    ? '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  fontWeight: currentView === 'dashboard' ? '700' : '500',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  fontSize: isMobile ? '0.9375rem' : '1rem',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  if (currentView !== 'dashboard') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentView !== 'dashboard') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <i className="fas fa-home" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                  transition: 'transform 0.3s ease'
                }}></i>
                Dashboard
              </button>
            )}

            {/* Create Member - Admin Only */}
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/create-member')}
                className="btn-3d"
                style={{
                  padding: buttonPadding, // USING NEW VARIABLE
                  background: location.pathname === '/create-member'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  borderRadius: isMobile ? '0.875rem' : '0.75rem',
                  border: `1px solid ${location.pathname === '/create-member' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: buttonGap, // USING NEW VARIABLE
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '100%',
                  textAlign: 'left',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: location.pathname === '/create-member'
                    ? '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  fontWeight: location.pathname === '/create-member' ? '700' : '500',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  fontSize: isMobile ? '0.9375rem' : '1rem',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  if (location.pathname !== '/create-member') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (location.pathname !== '/create-member') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <i className="fas fa-user-plus" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }}></i>
                Create Member
              </button>
            )}

            {/* Assign Task - Admin Only */}
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/assign-task')}
                className="btn-3d"
                style={{
                  padding: buttonPadding, // USING NEW VARIABLE
                  background: currentView === 'assign-task'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  borderRadius: isMobile ? '0.875rem' : '0.75rem',
                  border: `1px solid ${currentView === 'assign-task' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: buttonGap, // USING NEW VARIABLE
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '100%',
                  textAlign: 'left',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: currentView === 'assign-task'
                    ? '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  fontWeight: currentView === 'assign-task' ? '700' : '500',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  fontSize: isMobile ? '0.9375rem' : '1rem',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  if (currentView !== 'assign-task') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentView !== 'assign-task') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <i className="fas fa-tasks" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }}></i>
                Assign Task
              </button>
            )}

            {/* View Tasks - Admin Only */}
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/view-tasks')}
                className="btn-3d"
                style={{
                  padding: buttonPadding, // USING NEW VARIABLE
                  background: location.pathname === '/view-tasks'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  borderRadius: isMobile ? '0.875rem' : '0.75rem',
                  border: `1px solid ${location.pathname === '/view-tasks' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: buttonGap, // USING NEW VARIABLE
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '100%',
                  textAlign: 'left',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: location.pathname === '/view-tasks'
                    ? '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  fontWeight: location.pathname === '/view-tasks' ? '700' : '500',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  fontSize: isMobile ? '0.9375rem' : '1rem',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  if (location.pathname !== '/view-tasks') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (location.pathname !== '/view-tasks') {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <i className="fas fa-list-check" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }}></i>
                View Tasks
              </button>
            )}
          </div>



          {/* Logout Button */}
          <button
            onClick={handleLogoutClick}
            className="btn-3d"
            style={{
              padding: isMobile ? '0.75rem 1.125rem' : '0.875rem',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)',
              color: 'white',
              borderRadius: isMobile ? '0.875rem' : '0.75rem',
              border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.5rem' : '0.625rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              marginTop: '0.5rem',
              marginBottom: '0',
              width: '100%',
              textAlign: 'left',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
              fontWeight: '600',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              minHeight: isMobile ? '42px' : '44px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.25) 100%)';
              e.currentTarget.style.transform = 'translateX(5px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)';
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.2)';
            }}
          >
            <i className="fas fa-sign-out-alt" style={{
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            }}></i>
            Logout
          </button>

          {/* Divider Bar below Logout (No changes needed) */}
          <div style={{
            marginTop: '0.5rem',
            width: '100%',
            height: '2px',
            background: isDarkMode
              ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 20%, rgba(255, 255, 255, 0.2) 80%, transparent 100%)'
              : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 20%, rgba(255, 255, 255, 0.3) 80%, transparent 100%)',
            borderRadius: '2px'
          }}></div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main style={{
        marginLeft: mainContentMargin,
        flex: '1',
        padding: isMobile ? '4.25rem 1.25rem 2rem 1.25rem' : '2rem',
        minHeight: '100vh',
        width: '100%',
        maxWidth: isMobile ? '100vw' : `calc(100vw - ${SIDEBAR_WIDTH_DESKTOP})`,
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
        className="page-transition"
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDarkMode
            ? 'radial-gradient(circle at 50% 30%, rgba(102, 126, 234, 0.08) 0%, transparent 60%)'
            : 'radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;